import { AsyncPipe } from '@angular/common'
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatMenuModule } from '@angular/material/menu'
import { CellLabelComponent } from '@app/feature/input-cells/cell-label/cell-label.component'
import { InputCellContainerComponent } from '@app/feature/input-cells/input-cell-container/input-cell-container.component'
import {
    DraftPinRecord,
    PinDraftRecord,
    PinRecordService,
} from '@app/feature/pin-panel/pin-record.service'
import {
    AccordionContentContext,
    FieldsCollectionComponent,
} from '@app/shared/fields-collection/fields-collection.component'
import { RecordFieldsComponent } from '@app/shared/record-fields/record-fields.component'
import { TypedTemplateDirective } from '@app/shared/typed-template.directive'
import { TbIconToggleButtonComponent } from '@components-library/tb-icon-toggle-button/tb-icon-toggle-button.component'
import { TbInputComponent } from '@components-library/tb-input/tb-input.component'
import { ModalContainerComponent } from '@components-library/tb-modal-manager/modal-container-component/modal-container.component'
import { generateUuid } from '@core/global-util'
import { Field, FieldEntities, FieldTypes, Schema, Workspace } from '@core/models'
import {
    CrudRecordModelFactoryService,
    FieldValue,
} from '@core/services/crud-record-model-factory.service'
import { RecordsService } from '@core/services/records.service'
import {
    FieldFacadeService,
    FieldTypeFacadeService,
    RecordFacadeService,
    SchemaFacadeService,
    UserFacadeService,
    WorkspaceFacadeService,
} from '@core/services/store-facade'
import { dirtyCheck } from '@ngneat/dirty-check-forms'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { combineLatest, of } from 'rxjs'
import { take } from 'rxjs/operators'
import { AddRecordSettingsSectionComponent } from './add-record-settings-section/add-record-settings-section.component'
import { TbButtonComponent } from '@components-library/tb-button/tb-button.component'
import { ModalLayoutComponent } from '@components-library/tb-modal-manager/modal-layout/modal-layout.component'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import { ModalLayoutButtonsComponent } from '@components-library/tb-modal-manager/modal-layout-buttons/modal-layout-buttons.component'
import { TbMenuListComponent } from '@components-library/tb-menu-list/tb-menu-list.component'
import { TbMenuListItemComponent } from '@components-library/tb-menu-list-item/tb-menu-list-item.component'
import { TbButtonWithOptionsComponent } from '@components-library/tb-button-with-options/tb-button-with-options.component'
import { FieldService } from '@services/entity-services/field/field.service'
import { toSignal } from '@angular/core/rxjs-interop'

export type AddRecordModalData = PinDraftRecord | { name?: string; schemaGuid?: string } | null

@UntilDestroy()
@Component({
    selector: 'app-add-record-content',
    templateUrl: './add-record-content.component.html',
    styleUrls: ['./add-record-content.component.sass'],
    imports: [
        TranslocoModule,
        ModalLayoutComponent,
        TbButtonComponent,
        AddRecordSettingsSectionComponent,
        ReactiveFormsModule,
        TbInputComponent,
        MatFormFieldModule,
        MatInputModule,
        FieldsCollectionComponent,
        TbIconToggleButtonComponent,
        MatMenuModule,
        TypedTemplateDirective,
        AsyncPipe,
        RecordFieldsComponent,
        InputCellContainerComponent,
        CellLabelComponent,
        ModalLayoutButtonsComponent,
        TbButtonWithOptionsComponent,
        TbMenuListComponent,
        TbMenuListItemComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddRecordContentComponent
    extends ModalContainerComponent<AddRecordModalData>
    implements OnInit, AfterViewInit
{
    pinRecordService = inject(PinRecordService)
    private readonly fieldSearchService = inject(FieldService)
    private readonly fieldFacadeService = inject(FieldFacadeService)
    private readonly recordService = inject(RecordsService)
    private readonly workspaceFacadeService = inject(WorkspaceFacadeService)
    private readonly schemaFacadeService = inject(SchemaFacadeService)
    private readonly fieldTypeFacadeService = inject(FieldTypeFacadeService)
    private readonly crudRecordModelFactory = inject(CrudRecordModelFactoryService)
    private readonly userFacadeService = inject(UserFacadeService)
    private readonly recordFacadeService = inject(RecordFacadeService)
    private readonly translation = inject(TranslocoService)
    draftRecord!: AddRecordModalData
    fieldTypes$ = this.fieldTypeFacadeService.selectFieldTypeEntities$
    workspaces$ = this.workspaceFacadeService.selectAllWorkspaces$
    schemas$ = this.schemaFacadeService.selectTableSchemas$
    records$ = this.recordFacadeService.selectRecordEntities$()
    selectedWorkspace$ = this.workspaceFacadeService.selectSelectedWorkspace$
    globalWorkspace$ = this.workspaceFacadeService.selectGlobalWorkspace$
    selectedSchema$ = this.schemaFacadeService.selectSelectedTableSchema$

    fields = toSignal(this.fieldFacadeService.selectFieldEntitiesBySchema$)

    schemaGuid!: string
    availableWorkspaces!: Workspace[]
    selectedWorkspace!: Workspace
    currentUserGuid!: string
    recordFieldValues: { [fieldGuid: string]: string } = {}
    nameField?: Field
    watchField?: Field
    richTextField?: Field
    descriptionField?: Field
    excludedFieldsGuids: string[] = []
    requiredNameError!: string
    isDirty = false
    isFavorite = false
    contextType = {} as AccordionContentContext

    formGroup!: FormGroup | undefined
    nameControl?: FormControl<string | null>
    descriptionControl?: FormControl<string | null>
    fullDescriptionControl?: FormControl<string | null>

    get isFormInvalid() {
        return this.formGroup?.invalid
    }

    constructor() {
        super()
        this.draftRecord = this.data
    }

    ngOnInit() {
        this.userFacadeService.currentUser$.pipe(take(1)).subscribe((user) => {
            if (!user) return

            this.currentUserGuid = user.guid
        })

        this.translation
            .selectTranslate('validator_errors.max_length', {
                requiredLength: '80',
                actualLength: '0',
                fieldName: 'Name',
            })
            .pipe(take(1))
            .subscribe((value) => (this.requiredNameError = value))

        if (this.draftRecord && 'data' in this.draftRecord) {
            this.availableWorkspaces = this.draftRecord.data.workspaces
            this.schemaGuid = this.draftRecord.data.schemaGuid
            this.recordFieldValues = this.draftRecord.data.fieldValues
            this.selectedWorkspace = this.draftRecord.data.selectedWorkspace
            this.setUniqueFields()
            if (this.watchField) {
                this.isFavorite = this.recordService.isFavorite(
                    this.recordFieldValues[this.watchField.guid],
                    this.currentUserGuid,
                )
            }
            this.setControls()
            return
        }

        combineLatest([this.selectedWorkspace$, this.globalWorkspace$, this.selectedSchema$])
            .pipe(take(1))
            .subscribe(([workspace, globalWorkspace, schema]) => {
                if (!schema) return

                this.selectedWorkspace = workspace

                const availableWorkspaces =
                    globalWorkspace.guid === workspace.guid
                        ? [workspace]
                        : [globalWorkspace, workspace]

                this.setRecordData(this.getSchemaFromData(schema), availableWorkspaces)

                if (this.draftRecord && 'name' in this.draftRecord) {
                    this.nameControl?.setValue(this.draftRecord.name ?? null)
                }
            })
    }

    ngAfterViewInit(): void {
        if (!this.formGroup) return

        dirtyCheck(this.formGroup, of(this.formGroup.value), {
            debounce: 0,
            useBeforeunloadEvent: false,
        })
            .pipe(untilDestroyed(this))
            .subscribe((isDirty) => {
                this.isDirty = isDirty
            })
    }

    hasChanged(): boolean {
        return this.isDirty
    }

    saveSettings({ workspaceGuids, schemaGuid }: { workspaceGuids: string[]; schemaGuid: string }) {
        this.workspaces$.pipe(take(1)).subscribe((workspaces) => {
            const findWorkspaces = workspaces.filter((workspace) =>
                workspaceGuids.includes(workspace.guid),
            )
            if (!findWorkspaces.length) return

            this.setRecordData(schemaGuid, findWorkspaces)
        })
    }

    addToFavorite() {
        if (this.watchField) {
            this.recordFieldValues[this.watchField.guid] = this.recordService.updateFavorite(
                this.recordFieldValues[this.watchField.guid],
                this.currentUserGuid,
            )
        }
    }

    createRecord() {
        if (this.isFormInvalid) {
            this.setInputError()
            return
        }

        this.addRecord().subscribe((data) => {
            if (data.status === 'success') this.close()
        })
    }

    createAndOpen() {
        if (this.isFormInvalid) {
            this.setInputError()
            return
        }

        this.addRecord().subscribe((result) => {
            if (result.status === 'success') {
                this.close()
                this.pinRecordService.openPinItem(result.recordGuid!)
            }
        })
    }

    createAndStartAnother() {
        if (this.isFormInvalid) {
            this.setInputError()
            return
        }

        this.addRecord().subscribe((data) => {
            if (data.status === 'success') {
                this.close()
                this.openAfterClose()
            }
        })
    }

    createAndDuplicate() {
        // TODO implement duplicate for support SOTs https://chat.anveo.com/git/AnveoTikibase_ui/issues/2770
        if (!this.nameField || !this.nameControl) return
        if (this.isFormInvalid) {
            this.setInputError()
            return
        }

        this.addRecord().subscribe(() => {
            const name = `${this.recordFieldValues[this.nameField!.guid]} copy`
            this.nameControl!.setValue(name)

            const recordData = {
                name,
                data: {
                    name,
                    workspaces: this.availableWorkspaces,
                    selectedWorkspace: this.selectedWorkspace,
                    schemaGuid: this.schemaGuid,
                    fieldValues: this.recordFieldValues,
                },
                guid: generateUuid(),
            }

            this.modalFlowManagerService
                .openDialog<AddRecordContentComponent, PinDraftRecord | { schemaGuid: string }>({
                    component: AddRecordContentComponent,
                    data: recordData,
                    startFlow: true,
                })
                .pipe(take(1))
        })
    }

    pinDraftRecord() {
        if (!this.nameField) return
        const nameValue = this.recordFieldValues[this.nameField!.guid]

        const draftRecordData: DraftPinRecord = {
            name: nameValue ? nameValue : null,
            workspaces: this.availableWorkspaces,
            selectedWorkspace: this.selectedWorkspace,
            schemaGuid: this.schemaGuid,
            fieldValues: this.recordFieldValues,
        }
        if (this.draftRecord && 'guid' in this.draftRecord) {
            this.pinRecordService.updateDraftRecord(this.draftRecord.guid, draftRecordData)
        } else {
            this.pinRecordService.pinDraftRecord(draftRecordData)
        }

        this.modalManagerService.close()
    }

    removePinRecord() {
        if (this.draftRecord && 'guid' in this.draftRecord) {
            this.pinRecordService.removePinRecord(this.draftRecord.guid)
        }
    }

    private setInputError() {
        this.errors = [this.translation.translate('dialog_errors.incorrect_input')]
    }

    private getSchemaFromData(schema: Schema) {
        if (this.draftRecord && 'schemaGuid' in this.draftRecord) {
            return this.draftRecord.schemaGuid || schema.guid
        }

        return schema.guid
    }

    private addRecord() {
        this.accumulateRecordFieldValuesOnSave()
        const record = this.crudRecordModelFactory.prepareCreateModelWithFields(
            '',
            this.schemaGuid,
            this.availableWorkspaces.map((workspace) => workspace.guid),
            this.prepareFieldValues(),
        )

        this.removePinRecord()

        return this.wrapResponseObservable(this.recordService.createRecord(record))
    }

    private prepareFieldValues() {
        return Object.keys(this.recordFieldValues)
            .map((key) => {
                return {
                    fieldGuid: key,
                    value: this.recordFieldValues[key],
                } as FieldValue
            })
            .filter((item) => item.fieldGuid)
            .filter((item) => item.value.length)
    }

    private accumulateRecordFieldValuesOnSave() {
        this.parseControlsToRecordFieldValues('required')
        this.parseControlsToRecordFieldValues('shared')
        this.availableWorkspaces.forEach((workspace) => {
            this.parseControlsToRecordFieldValues(workspace.guid)
        })
    }

    private parseControlsToRecordFieldValues(controlName: string) {
        const formGroup = this.formGroup?.value[controlName]

        if (!formGroup) return

        Object.keys(formGroup).forEach((key) => {
            this.recordFieldValues[key] = formGroup[key]
        })
    }

    private setRecordData(schemaGuid: string, selectedWorkspaces: Workspace[]) {
        if (this.schemaGuid !== schemaGuid) {
            this.recordFieldValues = this.setRecordFieldValues(this.fields()![schemaGuid])

            this.schemaGuid = schemaGuid
            this.setUniqueFields()
            this.setControls()
        }

        this.availableWorkspaces = selectedWorkspaces
    }

    private openAfterClose() {
        this.modalManagerService
            .openDialog<AddRecordContentComponent, PinDraftRecord | null>({
                component: AddRecordContentComponent,
            })
            .pipe(take(1))
    }

    private setRecordFieldValues(fieldEntities: FieldEntities) {
        return Object.keys(fieldEntities).reduce(
            (acc, key) => {
                acc[key] = this.getValue(fieldEntities[key])
                return acc
            },
            {} as { [guid: string]: string },
        )
    }

    private setUniqueFields() {
        // In case this method is called on update, we should clear the previous values
        this.richTextField = undefined
        this.descriptionField = undefined

        const fields = this.fields()

        if (!fields) return

        const fieldEntities = fields[this.schemaGuid]

        this.nameField = this.fieldSearchService.findImportantFieldByType(
            fieldEntities,
            FieldTypes.NAME,
        )
        this.watchField = this.fieldSearchService.findImportantFieldByType(
            fieldEntities,
            FieldTypes.WATCH,
        )
        this.richTextField = this.fieldSearchService.findImportantFieldByType(
            fieldEntities,
            FieldTypes.RICH_TEXT,
        )
        if (this.richTextField) {
            this.excludedFieldsGuids.push(this.richTextField.guid)
        }

        this.descriptionField = this.fieldSearchService.findFieldByType(
            fieldEntities,
            FieldTypes.DESCRIPTION,
            this.selectedWorkspace,
        )
        if (this.descriptionField) {
            this.excludedFieldsGuids.push(this.descriptionField.guid)
        }
    }

    private setControls() {
        if (this.formGroup) {
            this.resetControls()
            return
        }

        this.formGroup = new FormGroup({})
        this.setUniqueControls()
        if (this.nameControl) {
            this.formGroup.setControl('name', this.nameControl)
        }
        if (this.descriptionControl) {
            this.formGroup.setControl('description', this.descriptionControl)
        }
    }

    private resetControls() {
        if (!this.formGroup) return

        this.formGroup = new FormGroup({})

        if (this.nameControl) {
            this.formGroup.setControl('name', this.nameControl)
        }
        if (this.descriptionControl) {
            this.formGroup.setControl('description', this.descriptionControl)
        }
        this.formGroup.updateValueAndValidity()
    }

    private setUniqueControls() {
        if (!this.nameField && !this.richTextField && !this.descriptionField) return

        if (this.nameField) {
            this.nameControl = new FormControl(this.recordFieldValues[this.nameField.guid], {
                validators: [Validators.required, Validators.maxLength(80)],
            })

            this.nameControl.valueChanges.pipe(untilDestroyed(this)).subscribe((change) => {
                if (this.nameField) {
                    this.recordFieldValues[this.nameField.guid] = change ?? ''
                }
            })
        }

        if (this.descriptionField) {
            const descriptionField = this.descriptionField

            const validators = descriptionField.is_required ? [Validators.required] : null

            this.descriptionControl = new FormControl(
                this.recordFieldValues[descriptionField.guid],
                validators,
            )

            this.descriptionControl.valueChanges.pipe(untilDestroyed(this)).subscribe((change) => {
                this.recordFieldValues[descriptionField.guid] = change ?? ''
            })
        }

        if (this.richTextField) {
            const richTextField = this.richTextField

            const validators = richTextField.is_required ? [Validators.required] : null

            this.fullDescriptionControl = new FormControl(
                this.recordFieldValues[richTextField.guid],
                validators,
            )

            this.fullDescriptionControl.valueChanges
                .pipe(untilDestroyed(this))
                .subscribe((change) => {
                    this.recordFieldValues[richTextField.guid] = change ?? ''
                })
        }
    }

    private getValue(field: Field) {
        return field.default_value ?? ''
    }
}
